import { useState } from "react";
import { Link } from "react-router-dom";
import { Play, Mic, BarChart3, MessageSquare, Sparkles, ArrowUpRight, Lock, ExternalLink } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import EarlyAccessCta from "@/components/site/EarlyAccessCta";
import DemoModal from "@/components/site/DemoModal";
import { DEMO_URL } from "@/lib/api";

const featureRow = [
    {
        icon: Mic,
        title: "Live transcript",
        body: "Mike from Acme Co talks like a real prospect — budget hedges, vague timelines, the lot.",
    },
    {
        icon: BarChart3,
        title: "Score updates live",
        body: "Watch BANT or MEDDIC categories light up as the conversation moves — every 15 seconds.",
    },
    {
        icon: MessageSquare,
        title: "Next-best question",
        body: "AI surfaces three context-aware probing questions per turn. Tap to mark as asked.",
    },
    {
        icon: Sparkles,
        title: "Post-call summary",
        body: "When the call ends, see exactly what your reps would push to the CRM.",
    },
];

export default function Demo() {
    const [loaded, setLoaded] = useState(false);

    return (
        <div data-testid="demo-page" className="min-h-screen flex flex-col bg-white">
            <div className="bg-white border-b border-zinc-200">
                <Navbar tone="light" />
            </div>

            {/* INTRO */}
            <section className="bg-gradient-to-b from-zinc-50 to-white">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 pt-16 pb-10 md:pt-20 md:pb-14">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white/80 backdrop-blur px-3 py-1.5 text-xs shadow-sm">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 live-dot" />
                            <span className="text-zinc-700">Interactive demo · no signup · ~60 seconds</span>
                        </div>
                        <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-zinc-900">
                            Watch PipeGuide score a real qualification call.
                        </h1>
                        <p className="mt-5 text-lg sm:text-xl text-zinc-600 leading-relaxed max-w-2xl">
                            Press <span className="font-semibold text-zinc-900">Play</span> below. We'll simulate
                            Mike from Acme Co talking to your rep — and you'll see exactly what reps see during
                            a real call. Score updates, suggested questions, the works.
                        </p>

                        <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-500">
                            <span className="flex items-center gap-2"><span className="h-1 w-1 rounded-full bg-zinc-400" /> No signup required</span>
                            <span className="flex items-center gap-2"><span className="h-1 w-1 rounded-full bg-zinc-400" /> No card on file</span>
                            <span className="flex items-center gap-2"><span className="h-1 w-1 rounded-full bg-zinc-400" /> Reset and replay anytime</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* THE EMBED */}
            <section data-testid="demo-embed-section" className="bg-white pb-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-8">
                    {/* Browser chrome frame around the iframe */}
                    <div className="rounded-2xl border border-zinc-200 bg-white shadow-2xl overflow-hidden">
                        <div className="flex items-center gap-2 border-b border-zinc-200 bg-zinc-50 px-4 py-2.5">
                            <div className="flex items-center gap-1.5">
                                <span className="h-2.5 w-2.5 rounded-full bg-zinc-300" />
                                <span className="h-2.5 w-2.5 rounded-full bg-zinc-300" />
                                <span className="h-2.5 w-2.5 rounded-full bg-zinc-300" />
                            </div>
                            <div className="mx-auto flex items-center gap-1.5 rounded-md bg-white border border-zinc-200 px-3 py-1 text-xs text-zinc-600">
                                <Lock className="h-3 w-3 text-zinc-400" />
                                <span className="font-mono">app.pipeguide.tech/demo</span>
                            </div>
                            <a
                                href={DEMO_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="demo-open-newtab"
                                className="text-xs text-zinc-500 hover:text-brand-primary inline-flex items-center gap-1 transition-colors"
                                aria-label="Open in new tab"
                            >
                                <ExternalLink className="h-3.5 w-3.5" />
                                <span className="hidden sm:inline">Open in new tab</span>
                            </a>
                        </div>

                        <div className="relative bg-zinc-50" style={{ height: "min(78vh, 820px)" }}>
                            {!loaded && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="text-center">
                                        <div className="mx-auto h-10 w-10 rounded-full border-2 border-zinc-200 border-t-brand-primary animate-spin" />
                                        <p className="mt-4 text-sm text-zinc-500">Loading interactive demo…</p>
                                    </div>
                                </div>
                            )}
                            <iframe
                                src={DEMO_URL}
                                title="PipeGuide interactive demo"
                                data-testid="demo-iframe"
                                onLoad={() => setLoaded(true)}
                                className="w-full h-full border-0"
                                loading="lazy"
                                allow="autoplay; clipboard-read; clipboard-write"
                            />
                        </div>
                    </div>

                    {/* Trouble loading fallback */}
                    <p className="mt-4 text-center text-xs text-zinc-500">
                        Demo not loading?{" "}
                        <a
                            href={DEMO_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="demo-fallback-link"
                            className="text-brand-primary hover:underline font-medium"
                        >
                            Open the full demo in a new tab
                            <ArrowUpRight className="inline h-3 w-3 ml-0.5" />
                        </a>
                    </p>
                </div>
            </section>

            {/* WHAT YOU'RE SEEING */}
            <section className="bg-zinc-50 border-y border-zinc-200">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 md:py-24">
                    <div className="max-w-2xl">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-primary">What you're seeing</p>
                        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900">
                            Everything reps experience — happening in the moment.
                        </h2>
                        <p className="mt-4 text-lg text-zinc-600 leading-relaxed">
                            This is the same engine your team uses on real Zoom, Teams and Meet calls.
                            Same score logic. Same question generator. Same CRM-ready summary at the end.
                        </p>
                    </div>

                    <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {featureRow.map((f) => (
                            <div
                                key={f.title}
                                data-testid={`demo-feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                                className="rounded-xl border border-zinc-200 bg-white p-6 hover:border-brand-accent-muted hover:shadow-md transition-all"
                            >
                                <f.icon className="h-5 w-5 text-brand-primary" />
                                <h3 className="mt-4 font-display text-lg font-semibold text-zinc-900">{f.title}</h3>
                                <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{f.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-white relative overflow-hidden">
                <div className="absolute inset-0 bg-grid-light opacity-60" aria-hidden />
                <div className="absolute inset-x-0 top-0 h-[500px] bg-[radial-gradient(ellipse_70%_60%_at_50%_0%,rgba(14,78,90,0.08),transparent_70%)]" aria-hidden />
                <div className="relative max-w-4xl mx-auto px-6 sm:px-8 py-20 md:py-24 text-center">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-primary">Ready to try it for real?</p>
                    <h2 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900">
                        Run PipeGuide on your next call.
                    </h2>
                    <p className="mt-4 text-lg text-zinc-600 max-w-xl mx-auto">
                        Spin up a free account in under two minutes. 60-day free trial, no credit card,
                        and your account is seeded with a sample call so you can poke around first.
                    </p>
                    <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
                        <EarlyAccessCta
                            data-testid="demo-cta-start-free"
                            className="bg-brand-primary hover:bg-brand-hover text-white h-12 px-8 glow-blue"
                        />
                        <Button asChild variant="outline" data-testid="demo-cta-pricing" className="bg-white border-zinc-300 text-zinc-900 hover:bg-zinc-50 h-12 px-8">
                            <Link to="/pricing">See pricing</Link>
                        </Button>
                        <DemoModal
                            trigger={
                                <Button variant="outline" data-testid="demo-cta-book" className="bg-white border-zinc-300 text-zinc-900 hover:bg-zinc-50 h-12 px-8">
                                    Talk to sales
                                </Button>
                            }
                        />
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
